/**
 * `window.pipeexHostRibbon` is a single global slot and two routes compete for
 * it: the setup layout and the mapping workspace. React runs the outgoing
 * route's cleanup *after* the incoming route's effect, so an owner that
 * restored "the predecessor I captured on install" would resurrect a dead
 * handler over its own successor and send every later ribbon click to a route
 * that is no longer on screen.
 *
 * Command registration is therefore tokenised exactly like ribbon-state
 * ownership: the slot is installed once and forwards to whoever is current, and
 * only the owner whose token is still current may tear it down.
 */

import type { HostRibbonCommand, HostRibbonWindow } from './hostRibbonContract';

export type HostRibbonCommandHandler = (command: HostRibbonCommand) => void;

export interface HostRibbonCommandToken {
  readonly epoch: number;
  readonly owner: string;
}

interface HostRibbonCommandRegistration extends HostRibbonCommandToken {
  readonly dispatch: HostRibbonCommandHandler;
}

let lastClaimedEpoch = 0;
let registered: HostRibbonCommandRegistration | null = null;

const hostWindow = (): HostRibbonWindow => window as HostRibbonWindow;

/**
 * The installed slot holds no handler of its own. A reference the host shell
 * captured earlier keeps working and still reaches the current owner, so a
 * stale command object cannot address a retired route.
 */
const installCommandSlot = (): void => {
  hostWindow().pipeexHostRibbon = {
    dispatch: (command) => {
      registered?.dispatch(command);
    },
  };
};

const clearCommandSlot = (): void => {
  delete hostWindow().pipeexHostRibbon;
};

export const registerHostRibbonCommandHandler = (
  owner: string,
  dispatch: HostRibbonCommandHandler
): HostRibbonCommandToken => {
  lastClaimedEpoch += 1;
  registered = { epoch: lastClaimedEpoch, owner, dispatch };
  installCommandSlot();

  return { epoch: lastClaimedEpoch, owner };
};

export const isCurrentHostRibbonCommandOwner = (
  token: HostRibbonCommandToken | null
): boolean => token !== null && registered?.epoch === token.epoch;

/** Unregistering from a stale token is a no-op: the newer owner keeps the slot. */
export const unregisterHostRibbonCommandHandler = (token: HostRibbonCommandToken): boolean => {
  if (!isCurrentHostRibbonCommandOwner(token)) {
    return false;
  }

  registered = null;
  clearCommandSlot();

  return true;
};

/** Which route commands currently reach; for diagnostics and tests. */
export const currentHostRibbonCommandOwner = (): string | null => registered?.owner ?? null;

/** Test seam: forget a registration a previous render left behind. */
export const resetHostRibbonCommandRegistry = (): void => {
  registered = null;
  clearCommandSlot();
};
